import { InAppState } from "../base/InAppState";

// Tree-shaken ECharts, as on the Übersicht (see IndexState).
import * as echarts from "echarts/core";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";

import template from "./AiCostsTemplate.html";
import { _lcs } from "../../i18n";
import { AiCostFormat as F, PAYER_COLORS, MODEL_COLORS } from "./AiCostFormat";

echarts.use( [ BarChart, LineChart, PieChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer ] );

/**
 * AiCosts — "KI-Kosten": what Vertex AI costs the platform, from the aiUsage
 * ledger (GET /admin/ai-cost).
 *
 * Twelve months of totals (split by feature, payer or model), the daily trend
 * of the last 60 days, and for one selected month: the ten most expensive
 * clients with their budget use, the split by feature and by model. A client
 * row opens AiCostsDetail; so does the "Plattform-eigene Kosten" row, for the
 * calls no client is charged for.
 *
 * Amounts are EUR at the API's fixed rate — the rate client budgets are
 * checked with — and list-price estimates, not Google's invoice.
 */
export class AiCosts extends InAppState
{
    static ROUTE = '/admin/ai-costs';
    static ID = 'admin-ai-costs';

    async enter()
    {
        this.charts = [];
        this.data = null;
        this.month = null;
        this.split = "feature";

        const root = this.app.view.pageContent;
        this.addEventListener( root, "click", ( e ) => this.onClick( e ) );
        this.addEventListener( root, "keydown", ( e ) =>
        {
            if ( ( e.key === "Enter" || e.key === " " ) && e.target.closest( "tr[data-org]" ) )
            {
                e.preventDefault();
                this.openClient( e.target.closest( "tr[data-org]" ).dataset.org );
            }
        } );
        this.addEventListener( root, "change", ( e ) =>
        {
            if ( e.target.matches( '[data-id="month"]' ) )
            {
                this.month = e.target.value;
                this.render();
            }
        } );
        this.addEventListener( window, "resize", () => this.charts.forEach( ( c ) => c.resize() ) );

        this.renderState( "loading" );

        try
        {
            const { result } = await this.app.api.get( "/admin/ai-cost" );
            if ( !result || +result.status !== 200 || !result.data || !Array.isArray( result.data.months ) )
            {
                throw new Error( "Unexpected /admin/ai-cost response." );
            }
            this.data = result.data;
        }
        catch ( e )
        {
            console.error( e );
        }

        if ( !this.isActive() )
        {
            return;
        }
        if ( !this.data )
        {
            this.renderState( "error" );
            return;
        }

        this.month = this.data.months[ this.data.months.length - 1 ].month;
        this.render();
    }

    async exit()
    {
        this.disposeCharts();
        await super.exit();
    }

    disposeCharts()
    {
        ( this.charts || [] ).forEach( ( c ) => c.dispose() );
        this.charts = [];
    }

    renderState( state )
    {
        this.disposeCharts();
        this.app.view.render( this.app.view.pageContent, template, { state } );
    }

    /* ------------------------------ Rendering ------------------------------- */

    render()
    {
        const d = this.data;
        const months = d.months;
        const index = Math.max( 0, months.findIndex( ( m ) => m.month === this.month ) );
        const month = months[ index ];
        const previous = index > 0 ? months[ index - 1 ] : null;
        const isCurrent = index === months.length - 1;
        const monthName = F.month( month.month );

        // The current month is compared by its forecast: half a month against
        // a whole one would always read as a drop.
        const forecast = isCurrent ? F.forecast( month.costEur ) : month.costEur;
        const delta = previous && previous.costEur > 0 ? ( forecast - previous.costEur ) / previous.costEur : null;

        const clientsTotal = month.clientsEur || 0;
        const topClients = ( month.topClients || [] ).map( ( c, i ) =>
        {
            const percent = c.budgetShare === null || c.budgetShare === undefined ? null : Math.round( c.budgetShare * 100 );
            return {
                "rank" : i + 1,
                "organisationId" : c.organisationId,
                "name" : c.isDeleted ? _lcs( "Gelöschter Gastgeber" ) : ( c.name || _lcs( "Gastgeber ohne Namen" ) ),
                "isDeleted" : !!c.isDeleted,
                "email" : c.email,
                "requests" : F.n( c.requests ),
                "cost" : F.money( c.costEur ),
                "budget" : F.money( c.budgetEur ),
                "budgetPercent" : percent,
                "budgetTone" : percent === null ? "ok" : ( percent >= 100 ? "err" : ( percent >= 80 ? "warn" : "ok" ) ),
                "share" : clientsTotal > 0 ? F.percent( c.costEur / clientsTotal ) : "—"
            };
        } );

        const shownTotal = ( month.topClients || [] ).reduce( ( sum, c ) => sum + c.costEur, 0 );
        const clientNote = month.clientCount > topClients.length
            ? _lcs( "{shown} von {count} Gastgebern mit KI-Kosten · die übrigen zusammen {rest}", {
                shown : topClients.length, count : month.clientCount, rest : F.money( Math.max( clientsTotal - shownTotal, 0 ) ) } )
            : _lcs( "{count} Gastgeber mit KI-Kosten in diesem Monat", { count : month.clientCount, n : F.n( month.clientCount ) } );

        this.disposeCharts();
        this.app.view.render( this.app.view.pageContent, template, {
            "state" : "ready",
            "split" : this.split,
            "monthName" : monthName,
            "monthOptions" : months.slice().reverse().map( ( m ) => ( {
                "value" : m.month, "label" : F.month( m.month ), "selected" : m.month === month.month
            } ) ),
            "rateNote" : _lcs( "Schätzung nach Listenpreis · 1 $ = {rate} €", { rate : F.n( d.eurPerUsd ) } ),
            "kpi" : {
                "monthLabel" : isCurrent ? _lcs( "Diesen Monat" ) : monthName,
                "month" : F.money( month.costEur ),
                "delta" : delta === null
                    ? _lcs( "kein Vergleich zum Vormonat" )
                    : _lcs( "{delta} ggü. Vormonat ({previous})", {
                        delta : ( delta >= 0 ? "+" : "−" ) + F.percent( Math.abs( delta ) ), previous : F.money( previous.costEur ) } ),
                "deltaClass" : delta === null ? "kpi__delta--flat" : ( delta > 0.25 ? "kpi__delta--warn" : "kpi__delta--flat" ),
                "deltaIcon" : delta !== null && delta < 0 ? "chevron-down" : "chevron-up",
                "forecastLabel" : isCurrent ? _lcs( "Prognose Monatsende" ) : _lcs( "Anfragen" ),
                "forecast" : isCurrent ? F.money( forecast ) : F.n( month.requests ),
                "forecastSub" : isCurrent
                    ? _lcs( "{count} Anfragen bisher", { count : month.requests, n : F.n( month.requests ) } )
                    : _lcs( "{n} fehlgeschlagen", { n : F.n( month.errors ) } ),
                "clients" : F.money( month.clientsEur ),
                "platform" : F.money( month.platformEur ),
                "clientCount" : _lcs( "{count} Gastgeber mit KI-Kosten", { count : month.clientCount || 0, n : F.n( month.clientCount ) } ),
                "total" : F.money( d.totals.costEur ),
                "totalSub" : F.usd( d.totals.costUsd ) + " · " + _lcs( "{count} Anfragen", { count : d.totals.requests, n : F.n( d.totals.requests ) } )
            },
            "unpriced" : d.totals.unpriced || 0,
            "unpricedText" : F.n( d.totals.unpriced ),
            "topClients" : topClients,
            "clientNote" : clientNote,
            "monthFeatures" : month.byFeature,
            "monthModels" : month.byModel.map( ( m ) => ( {
                "model" : m.model, "requests" : F.n( m.requests ), "cost" : F.money( m.costEur )
            } ) ),
            "tokenNote" : _lcs( "{input} Eingabe- und {output} Ausgabe-Tokens", {
                input : F.compact( month.inputTokens ), output : F.compact( month.outputTokens ) } )
        } );

        this.drawCharts( month );
    }

    drawCharts( month )
    {
        const root = this.app.view.pageContent;
        const draw = ( id, option ) =>
        {
            const el = root.querySelector( '[data-id="' + id + '"]' );
            if ( !el ) return;
            const chart = echarts.init( el );
            chart.setOption( option );
            this.charts.push( chart );
        };

        const months = this.data.months;
        draw( "chart-months", F.monthlyStackOption( months.map( ( m ) => m.month ), this.monthSeries( months ) ) );
        draw( "chart-days", F.dailyOption( this.data.days ) );
        draw( "chart-features", F.donutOption(
            month.byFeature.map( ( f, i ) => ( { name : F.feature( f.feature ), value : f.costEur, color : F.featureColor( f.feature, i ) } ) ),
            _lcs( "im Monat" ) ) );
    }

    /** The stacked series for the monthly chart, by the selected split. */
    monthSeries( months )
    {
        if ( this.split === "payer" )
        {
            return [
                { name : _lcs( "Gastgeber" ), color : PAYER_COLORS.clients, values : months.map( ( m ) => m.clientsEur ) },
                { name : _lcs( "Plattform" ), color : PAYER_COLORS.platform, values : months.map( ( m ) => m.platformEur ) }
            ];
        }

        const key = this.split === "model" ? "model" : "feature";
        const list = this.split === "model" ? this.data.byModel : this.data.byFeature;
        return list.map( ( item, i ) => ( {
            name : key === "model" ? item.model : F.feature( item.feature ),
            color : key === "model" ? MODEL_COLORS[ i % MODEL_COLORS.length ] : F.featureColor( item.feature, i ),
            values : months.map( ( m ) =>
            {
                const hit = ( key === "model" ? m.byModel : m.byFeature ).find( ( x ) => x[ key ] === item[ key ] );
                return hit ? hit.costEur : 0;
            } )
        } ) );
    }

    /* ------------------------------- Actions -------------------------------- */

    onClick( e )
    {
        const split = e.target.closest( "[data-split]" );
        if ( split )
        {
            this.split = split.dataset.split;
            this.render();
            return;
        }
        const org = e.target.closest( "[data-org]" );
        if ( org )
        {
            e.preventDefault();
            this.openClient( org.dataset.org );
        }
    }

    openClient( organisationId )
    {
        this.app.router.redirect( AiCosts.ROUTE + "/" + encodeURIComponent( organisationId ) );
    }

    getContentHeaderData()
    {
        return {
            title : _lcs( "KI-Kosten" ),
            subtitle : _lcs( "Vertex AI · Plattform und Gastgeber" )
        };
    }
}
